import { useState, useEffect, useLayoutEffect, Fragment } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { getOrgTimezone, formatDateTime } from "../lib/timezone";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { DatePicker } from "../components/DatePicker";
import { toast } from "sonner";
import {
  CreditCard, Eye, Filter, X, Trash2, Loader2, AlertTriangle,
  TrendingUp, Calendar, Clock, Search, ChevronDown, Lock, User, History, Package,
  ChevronLeft, ChevronRight, Phone,
} from "lucide-react";
import { WhatsApp } from "../components/ui/whatsapp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function useIsDesktop() {
  const mq = typeof window !== "undefined"
    ? window.matchMedia("(min-width: 640px)")
    : null;
  const [isDesktop, setIsDesktop] = useState(mq ? mq.matches : true);
  useLayoutEffect(() => {
    if (!mq) return;
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

export default function Payments() {
  const { organization, currentBranch } = useAuth();
  const navigate = useNavigate();
  const orgTimezone = getOrgTimezone(organization);
  const isDesktop = useIsDesktop();

  const [payments, setPayments] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [monthAmount, setMonthAmount] = useState(0);

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ packageId: "", fromDate: "", toDate: "", search: "" });

  const [deletePayment, setDeletePayment] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const toggleRow = (id) => setExpandedRowId(prev => (prev === id ? null : id));

  useEffect(() => { fetchPackages(); }, []);
  useEffect(() => { fetchData(); }, [page, limit, currentBranch, filters]);

  const fetchPackages = async () => {
    try { const res = await api.get("/packages"); setPackages(res.data.packages || []); } catch { }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      if (filters.packageId) params.append("packageId", filters.packageId);
      if (filters.fromDate) params.append("fromDate", filters.fromDate);
      if (filters.toDate) params.append("toDate", filters.toDate);
      if (filters.search) params.append("name", filters.search);

      const res = await api.get(`/memberships/payments/all?${params.toString()}`);
      const data = res.data;
      setPayments(data.payments || []);
      setTotal(data.total || 0);
      setTotalAmount(data.totalAmount || 0);
      setMonthAmount(data.monthAmount || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load payments");
    } finally { setLoading(false); }
  };

  const clearFilters = () => {
    setFilters({ packageId: "", fromDate: "", toDate: "", search: "" });
    setPage(1);
  };

  const handleDelete = async () => {
    if (!deletePayment) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/memberships/payments/${deletePayment.id}`);
      toast.success("Payment deleted");
      setDeletePayment(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete payment");
    } finally { setDeleteLoading(false); }
  };

  const activeFilterCount = [filters.packageId, filters.fromDate, filters.toDate, filters.search].filter(v =>
    Array.isArray(v) ? v.length > 0 : !!v
  ).length;
  const hasActiveFilters = activeFilterCount > 0;

  const renderDate = (dateStr) => {
    if (!dateStr) return <span className="text-muted-foreground">—</span>;
    return (
      <span className="font-mono text-sm tracking-tight">
        {formatDateTime(dateStr, orgTimezone)}
      </span>
    );
  };

  const renderPaymentDetails = (p) => {
    return (
      <div className="text-sm text-foreground bg-secondary/5">
        {p.member?.phone && (
          <div className="grid grid-cols-2 gap-2 p-2 border-b border-border">
            <a
              href={`tel:${p.member.phone}`}
              onClick={(e) => e.stopPropagation()}
              className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-sky-400 dark:hover:border-sky-500 hover:shadow-md active:scale-95 px-2"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-50 dark:bg-sky-950/50 transition-colors group-hover:bg-sky-100 dark:group-hover:bg-sky-900/50">
                <Phone className="h-4 w-4 text-sky-600 dark:text-sky-400" />
              </div>
              <div className="flex flex-col items-start leading-tight min-w-0">
                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                  Mobile Call
                </span>
                <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                  Call Now
                </span>
              </div>
            </a>
            <a
              href={`https://wa.me/${p.member.phone}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md active:scale-95 px-2"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/50 transition-colors group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50">
                <WhatsApp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex flex-col items-start leading-tight min-w-0">
                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                  Message
                </span>
                <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                  WhatsApp
                </span>
              </div>
            </a>
          </div>
        )}
        <div className="flex items-center gap-2 p-2 border-b border-border/40 bg-secondary/10">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/members/${p.member?.id}`);
            }}
          >
            <User className="h-3.5 w-3.5" /><span className="hidden md:block">View Member</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors text-primary border-primary/20"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/memberships/${p.membershipId}`);
            }}
          >
            <CreditCard className="h-3.5 w-3.5" /> <span className="">View Membership</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors text-destructive hover:text-destructive border-red-500/20"
            onClick={(e) => {
              e.stopPropagation();
              setDeletePayment(p);
            }}
          >
            <Trash2 className="h-3.5 w-3.5" /> <span className="hidden md:block">Delete Payment</span>
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
          <div className="space-y-1">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Member & Payment Info</p>
            <div className="text-xs space-y-1 mt-1 text-muted-foreground">
              <p><span className="font-mono text-muted-foreground">Name:</span> <span className="font-semibold text-foreground">{p.member?.name || "—"}</span></p>
              <p><span className="font-mono text-muted-foreground">Phone:</span> <span className="font-mono">{p.member?.phone || "—"}</span></p>
              <p><span className="font-mono text-muted-foreground">Date:</span> <span className="font-mono">{formatDateTime(p.date, orgTimezone)}</span></p>
              <div><span className="font-mono text-muted-foreground">Payment Mode:</span> <Badge variant="outline" className={`text-[10px] py-0 px-1 font-mono border ${modeColor(p.paymentMode)}`}>{p.paymentMode}</Badge></div>
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Transaction Details</p>
            <div className="text-xs space-y-1 mt-1 text-muted-foreground">
              <p><span className="font-mono text-muted-foreground">Payment ID:</span> <span className="font-mono">{p.id}</span></p>
              <p><span className="font-mono text-muted-foreground">Transaction ID:</span> <span className="font-mono">{p.transactionId || "—"}</span></p>
              <p><span className="font-mono text-muted-foreground">Package:</span> <span>{p.packageName || "—"}</span></p>
              <p><span className="font-mono text-muted-foreground">Amount:</span> <span className="font-mono font-bold text-emerald-500">₹{p.amount?.toLocaleString()}</span></p>
            </div>
          </div>
          <div className="space-y-1 col-span-1 md:col-span-1">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Notes</p>
            <p className="text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap mt-1">
              {p.notes || "No payment-specific notes provided."}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const modeColor = (mode) => {
    const map = {
      Cash: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      Card: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      UPI: "bg-violet-500/10 text-violet-500 border-violet-500/20",
      "Bank Transfer": "bg-amber-500/10 text-amber-500 border-amber-500/20",
    };
    return map[mode] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/20";
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-2 mt-2 md:space-y-3" data-testid="payments-page">

      {/* Header */}
      <div className="flex flex-row sm:items-center justify-between gap-2 md:gap-4">
        <div>
          <h1 className="text-xl md:text-4xl font-black font-heading tracking-tight uppercase">Payments</h1>
          <p className="text-xs tracking-tight text-muted-foreground mt-1 hidden md:block">All membership payment transactions</p>
        </div>
        <Button
          variant={hasActiveFilters ? "secondary" : "outline"}
          size="sm"
          onClick={() => setShowFilters(true)}
          className="font-heading uppercase tracking-wide text-xs relative"
          data-testid="toggle-filters-btn"
        >
          <Filter className="h-4 w-4 mr-2" /><span className="block">Filters</span>
          {activeFilterCount > 0 && (
            <Badge className="ml-1.5 bg-primary text-primary-foreground h-4 min-w-4 px-1 rounded-full text-[10px] leading-none flex items-center justify-center">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2 p-3">
            <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2 text-muted-foreground">
              <TrendingUp className="h-4 w-4 text-emerald-500" /> Total Payments
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 ">
            <p className="text-xl md:text-3xl font-black font-mono text-emerald-500">
              ₹{totalAmount.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground mt-1 font-mono">{total} transactions</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2 p-3 ">
            <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4 text-primary" /> This Month
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <p className="text-xl md:text-3xl font-black font-mono text-primary">
              ₹{monthAmount.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground mt-1 font-mono">
              {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ── Filter Drawer – bottom on mobile, right on desktop ── */}
      <Sheet open={showFilters} onOpenChange={setShowFilters}>
        <SheetContent
          side={isDesktop ? "right" : "bottom"}
          className={isDesktop
            ? "w-80 flex flex-col gap-0 p-0"
            : "flex flex-col gap-0 p-0 rounded-t-2xl max-h-[92vh]"}
        >
          <SheetHeader className="px-6 py-5 border-b border-border shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-heading uppercase tracking-wide text-base flex items-center gap-2">
                <Filter className="h-4 w-4" /> Filters
                {activeFilterCount > 0 && (
                  <Badge className="bg-primary text-primary-foreground rounded-full text-[10px] h-5 px-1.5">
                    {activeFilterCount} active
                  </Badge>
                )}
              </SheetTitle>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                  Clear all
                </button>
              )}
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
            {/* Search */}
            <div className="space-y-2">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Search Member</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Member name..."
                  value={filters.search}
                  onChange={(e) => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Package */}
            <div className="space-y-2">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Package</Label>
              <Select
                value={filters.packageId || "all"}
                onValueChange={(v) => { setFilters(f => ({ ...f, packageId: v === "all" ? "" : v })); setPage(1); }}
              >
                <SelectTrigger><SelectValue placeholder="All Packages" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Packages</SelectItem>
                  {packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Date range – 2 cols on mobile to save space */}
            <div className={`grid gap-4 ${!isDesktop ? "grid-cols-2" : "grid-cols-1"}`}>
              <div className="space-y-2">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">From Date</Label>
                <DatePicker
                  value={filters.fromDate}
                  onChange={(v) => { setFilters(f => ({ ...f, fromDate: v })); setPage(1); }}
                  placeholder="Start date"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">To Date</Label>
                <DatePicker
                  value={filters.toDate}
                  onChange={(v) => { setFilters(f => ({ ...f, toDate: v })); setPage(1); }}
                  placeholder="End date"
                />
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── List / Table Card ── */}
      <Card className="bg-card border-x-0 md:border border-y border-border overflow-hidden flex flex-col flex-1 min-h-0 md:mx-0 rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
            <span className="text-muted-foreground text-sm">Loading...</span>
          </div>
        ) : payments.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
            No payments found
          </div>
        ) : (
          <>
            {/* ── Mobile card list (hidden md+) ── */}
            <div className="md:hidden overflow-y-auto flex-1 divide-y divide-border">
              {payments.map((p, idx) => (
                <Collapsible
                  key={p.id}
                  open={expandedRowId === p.id}
                  onOpenChange={() => toggleRow(p.id)}
                  asChild
                >
                  <div className={` ${idx !== payments.length - 1 ? "" : "border-b border-border"} hover:bg-secondary/10 transition-colors`}>
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between gap-2 cursor-pointer select-none px-3 py-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="flex items-center justify-between flex-col shrink-0">
                            <Avatar className="h-8 w-8 border border-border">
                              {p.member?.photo ? <AvatarImage src={`${BACKEND_URL}${p.member.photo}`} /> : null}
                              <AvatarFallback className="bg-primary/10 text-primary text-xs font-heading font-bold">
                                {p.member?.name?.[0]?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <Button variant="ghost" size="icon" className="h-4 w-4 p-0 text-muted-foreground hover:text-foreground shrink-0 mt-0.5">
                              <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${expandedRowId === p.id ? "rotate-180" : ""}`} />
                            </Button>
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-sm leading-tight truncate">
                              {p.member?.name || "—"}
                            </p>
                            <p className="text-[11px] text-muted-foreground font-mono mt-1">
                              {formatDateTime(p.date, orgTimezone)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="flex items-end flex-col gap-0.5 text-right">
                            <Badge className={`text-[9px] font-mono border px-1.5 py-0 ${modeColor(p.paymentMode)}`}>
                              {p.paymentMode || "—"}
                            </Badge>
                            <div className="mt-1 leading-none font-mono">
                              <p className="font-bold text-sm text-emerald-500">
                                ₹{p.amount?.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border-t border-border/50 bg-secondary/5 animate-in fade-in slide-in-from-top-1 duration-200">
                        {renderPaymentDetails(p)}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>

            {/* ── Desktop table (hidden below md) ── */}
            <div className="hidden md:block overflow-auto flex-1 animate-in fade-in duration-200">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Package</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((p) => (
                    <Fragment key={p.id}>
                      <TableRow className="group cursor-pointer hover:bg-secondary/50 border-b border-border" onClick={() => toggleRow(p.id)}>
                        <TableCell className="w-10">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); toggleRow(p.id); }}>
                            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expandedRowId === p.id ? "rotate-180" : ""}`} />
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9 border border-border">
                              {p.member?.photo ? <AvatarImage src={`${BACKEND_URL}${p.member.photo}`} /> : null}
                              <AvatarFallback className="bg-primary/10 text-primary text-xs font-heading">
                                {p.member?.name?.[0]?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-sm">{p.member?.name || "—"}</p>
                              <p className="text-[10px] text-muted-foreground font-mono">{p.member?.phone}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{renderDate(p.date)}</TableCell>
                        <TableCell className="text-sm">
                          <span className="inline-flex items-center gap-1 text-sm font-medium">
                            <Package className="h-3.5 w-3.5 text-primary shrink-0" />
                            {p.packageName || "—"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs font-mono border ${modeColor(p.paymentMode)}`}>
                            {p.paymentMode || "—"}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono font-bold text-emerald-500">₹{p.amount?.toLocaleString()}</TableCell>
                        <TableCell className="max-w-[160px]">
                          {p.notes ? (
                            <span className="text-xs text-muted-foreground truncate block" title={p.notes}>
                              {p.notes}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                      {expandedRowId === p.id && (
                        <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border animate-in fade-in duration-200">
                          <TableCell colSpan={8} className="p-0">
                            {renderPaymentDetails(p)}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* Pagination */}
        {!loading && payments.length > 0 && (
          <div className="flex flex-row items-center justify-between gap-4 p-4 border-t border-border">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="font-mono hidden sm:block">{total} total</span>
              <div className="flex items-center gap-2">
                <span className="text-xs">Show:</span>
                <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
                  <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="sm:text-sm text-xs text-muted-foreground px-2">Page {page} of {Math.ceil(total / limit) || 1}</span>
              <Button variant="outline" size="sm" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletePayment} onOpenChange={(v) => { if (!v) setDeletePayment(null); }}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wide flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" /> Delete Payment
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete this payment? This action cannot be undone and the membership balance will be recalculated.
            </p>
            {deletePayment && (
              <div className="rounded-xl bg-secondary/40 border border-border p-4 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Member</span>
                  <span className="font-medium">{deletePayment.member?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-mono text-emerald-500">₹{deletePayment.amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-mono">{deletePayment.date?.slice(0, 10)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mode</span>
                  <span>{deletePayment.paymentMode}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeletePayment(null)} disabled={deleteLoading}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteLoading}
              className="font-heading uppercase tracking-wider">
              {deleteLoading
                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Deleting...</>
                : "Delete Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
